import type { TrackByFunction } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  forwardRef,
  input,
  output,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, map, of, startWith, take } from 'rxjs';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import type { AbstractControl } from '@angular/forms';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ControlValueAccessorDirective } from '../../directives/control-value-accessor.directive';
import { Option } from '../../misc/helpers';

const MAX_SELECT_OPTIONS_WITHOUT_SEARCH = 5;

type possibleTypes = boolean | string | number;

interface SelectOption extends Option<possibleTypes> {
  visible: boolean;
}

type IReturnType = possibleTypes | Array<possibleTypes>;

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    OverlayModule,
    ReactiveFormsModule,
  ],
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent extends ControlValueAccessorDirective<IReturnType> implements OnInit {
  trackByValue: TrackByFunction<SelectOption> = (_, c) => c.value;
  searchQuery = new FormControl<string>('');
  options$ = new BehaviorSubject<Option<possibleTypes>[]>([]);
  searchQuery$ = new BehaviorSubject<string | null>('');
  searchQueryResettable$: Observable<boolean> = this.searchQuery$.pipe(map(sq => !!sq));
  optionsWithVisibilityStatus$: Observable<SelectOption[]> = combineLatest([
    this.options$,
    this.searchQuery$,
  ]).pipe(
    map(([options, searchQuery]) => {
      const filteredOptions = searchQuery
        ? options.map(o => ({
            ...o,
            visible: o.label.toLowerCase().includes(searchQuery.toLowerCase()),
          }))
        : options.map(o => ({ ...o, visible: true }));

      return filteredOptions;
    })
  );
  filteredOptions$: Observable<Option<possibleTypes>[]> = this.optionsWithVisibilityStatus$.pipe(
    map(options => options.filter(o => o.visible))
  );

  searchable$: Observable<boolean> = this.options$.pipe(
    map(options => options.length > MAX_SELECT_OPTIONS_WITHOUT_SEARCH)
  );

  allSelected$ = new BehaviorSubject(false);

  private matSelect = viewChild(MatSelect);

  /** Closes the overlay panel (e.g. after picking a value outside the option list). */
  close(): void {
    this.matSelect()?.close();
  }

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set options(options: Option<possibleTypes>[] | undefined | null) {
    this.options$.next(options || []);
    // we need to reset the searchQuery otherwise we might not be able to reset it
    // when the options change to a list with less than 6 options
    this.searchQuery.reset();
  }

  filterInput = input<boolean>(true);
  multiSelect = input<boolean>(false);
  /** Extra class applied to the overlay panel, for per-usage styling (e.g. height). */
  panelClass = input<string>('');
  openedChange = output<boolean>();

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  set query(query: string) {
    this.searchQuery.setValue(query);
  }

  label = input.required<string>();

  get required() {
    const form_field = this.control;
    if (!form_field.validator) {
      return false;
    }

    //  Return the required state of the validator
    const validators = form_field.validator({} as AbstractControl) || {};
    return validators && validators['required'];
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.searchQuery.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(searchQuery => this.searchQuery$.next(searchQuery));

    combineLatest([
      this.filteredOptions$,
      this.control.valueChanges.pipe(startWith(this.control.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([options, value]) => {
        this.allSelected$.next(
          options.every(option => value?.toString().includes(option?.value?.toString()))
        );
      });
  }

  selectAll() {
    combineLatest([this.filteredOptions$, of(this.control.value)])
      .pipe(take(1))
      .subscribe(([filteredOptions, value]) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return;
        }
        const filteredValue = filteredOptions.map(option => option.value);
        const newValues = filteredValue.filter(fv => !value?.toString().includes(fv?.toString()));
        this.writeValue([...newValues, ...((value || []) as possibleTypes[])] as IReturnType);
      });
  }

  deselectAll() {
    combineLatest([this.filteredOptions$, of(this.control.value)])
      .pipe(take(1))
      .subscribe(([filteredOptions, value]) => {
        if (typeof value === 'string' || typeof value === 'number') {
          return;
        }
        const filteredValue = filteredOptions.map(option => option.value);
        const newValue = ((value as possibleTypes[]).filter(v => !filteredValue.includes(v)) ||
          []) as IReturnType;
        this.writeValue(newValue);
      });
  }

  clearSearchQuery() {
    this.searchQuery.reset();
  }
}
